import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Handshake, ShieldCheck, Crown, Star, Check, X,
  Loader2, RefreshCw, Search, Eye, Trash2,
  Building2, Heart, Dog, Briefcase, ThumbsUp, ThumbsDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import PartnerCodesSection from './PartnerCodesSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  ngo: 'Rescue / NGO', breeder: 'Breeder', business: 'Business', sponsor: 'Sponsor',
};
const TYPE_COLORS = {
  ngo: 'bg-rose-100 text-rose-800', breeder: 'bg-amber-100 text-amber-800',
  business: 'bg-blue-100 text-blue-800', sponsor: 'bg-purple-100 text-purple-800',
};
const STATUS_COLORS = {
  pending: 'bg-amber-500', approved: 'bg-green-500', rejected: 'bg-red-500', suspended: 'bg-slate-500',
};
const TIER_COLORS = {
  basic: '', verified: 'text-blue-600', premium: 'text-amber-600',
};

const PartnerAdmin = ({ darkMode = false }) => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [pendingCount, setPendingCount] = useState(0);
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const card = darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-card border';
  const cardText = darkMode ? 'text-white' : 'text-foreground';
  const mutedText = darkMode ? 'text-slate-400' : 'text-muted-foreground';
  const rowBg = darkMode ? 'bg-slate-700/40 hover:bg-slate-700/70' : 'bg-muted/40 hover:bg-muted/70';
  const inputBg = darkMode ? 'bg-slate-800 border-slate-700 text-white' : '';
  const dialogBg = darkMode ? 'bg-slate-800 border-slate-700 text-white' : '';

  const fetchPartners = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (typeFilter !== 'all') params.partner_type = typeFilter;
      const res = await axios.get(`${API}/partner-program/admin/all`, { params });
      setPartners(res.data.partners || []);
      setPendingCount(res.data.pending_count || 0);
    } catch (err) {
      console.error('Failed to fetch partners:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter]);

  useEffect(() => { fetchPartners(); }, [fetchPartners]);

  const handleAction = async (partnerId, action) => {
    try {
      await axios.post(`${API}/partner-program/admin/${partnerId}/${action}`);
      toast.success(`Partner ${action}d`);
      fetchPartners();
      if (selected?.id === partnerId) {
        setSelected(prev => ({
          ...prev,
          status: action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : prev.status,
          tier: action === 'verify' ? 'verified' : prev.tier,
        }));
      }
    } catch (err) {
      toast.error(`Failed to ${action} partner`);
    }
  };

  const handleDelete = async (partnerId) => {
    if (!window.confirm('Delete this partner permanently?')) return;
    try {
      await axios.delete(`${API}/partner-program/admin/${partnerId}`);
      toast.success('Partner deleted');
      fetchPartners();
      setDetailOpen(false);
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleToggleFeatured = async (partnerId) => {
    try {
      const res = await axios.post(`${API}/partner-program/admin/${partnerId}/feature`);
      toast.success(res.data.featured ? 'Partner featured' : 'Partner unfeatured');
      fetchPartners();
    } catch {
      toast.error('Failed to toggle featured');
    }
  };

  const handleUpdateNotes = async (partnerId, notes) => {
    try {
      await axios.put(`${API}/partner-program/admin/${partnerId}`, { admin_notes: notes });
      toast.success('Notes saved');
    } catch {
      toast.error('Failed to save notes');
    }
  };

  const filtered = searchQuery
    ? partners.filter(p =>
        p.organization_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.contact_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.city?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : partners;

  const formatDate = (d) => {
    try { return formatDistanceToNow(new Date(d), { addSuffix: true }); } catch { return d; }
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total', value: partners.length, color: cardText },
          { label: 'Pending', value: pendingCount, color: 'text-amber-500' },
          { label: 'Approved', value: partners.filter(p => p.status === 'approved').length, color: 'text-green-500' },
          { label: 'Verified+', value: partners.filter(p => ['verified', 'premium'].includes(p.tier)).length, color: 'text-blue-500' },
        ].map(s => (
          <Card key={s.label} className={card}>
            <CardContent className="p-3 text-center">
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
              <p className={`text-xs ${mutedText}`}>{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className={`pl-9 w-48 ${inputBg}`} />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className={`w-32 ${inputBg}`} data-testid="partner-admin-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className={`w-32 ${inputBg}`}>
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="ngo">NGOs</SelectItem>
            <SelectItem value="breeder">Breeders</SelectItem>
            <SelectItem value="business">Businesses</SelectItem>
            <SelectItem value="sponsor">Sponsors</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={fetchPartners} className={mutedText} data-testid="refresh-partners-btn">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Partner List */}
      <Card className={card}>
        <CardHeader>
          <CardTitle className={`${cardText} flex items-center gap-2`}>
            <Handshake className="w-5 h-5 text-amber-500" />
            Partners
            {pendingCount > 0 && (
              <Badge className="bg-amber-500 text-white ml-2">{pendingCount} Pending</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <div className={`text-center py-12 ${mutedText}`}>
              <Handshake className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p>No partners found</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map(p => (
                <div
                  key={p.id}
                  className={`flex items-center gap-3 p-3 rounded-lg ${rowBg} cursor-pointer transition-colors`}
                  onClick={() => { setSelected(p); setDetailOpen(true); }}
                  data-testid={`partner-admin-row-${p.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className={`${cardText} font-medium truncate`}>{p.organization_name}</p>
                      <Badge className={`${TYPE_COLORS[p.partner_type]} text-xs`}>{TYPE_LABELS[p.partner_type]}</Badge>
                      <Badge className={`${STATUS_COLORS[p.status]} text-white text-xs px-1.5 py-0`}>{p.status}</Badge>
                      {p.tier !== 'basic' && (
                        <span className={`text-xs font-medium ${TIER_COLORS[p.tier]}`}>
                          {p.tier === 'verified' && <ShieldCheck className="w-3 h-3 inline mr-0.5" />}
                          {p.tier === 'premium' && <Crown className="w-3 h-3 inline mr-0.5" />}
                          {p.tier}
                        </span>
                      )}
                      {p.featured && <Star className="w-3 h-3 text-amber-500 fill-amber-500" />}
                    </div>
                    <div className={`flex items-center gap-3 text-xs ${mutedText} mt-1`}>
                      {p.city && <span>{p.city}{p.state ? `, ${p.state}` : ''}</span>}
                      <span>{formatDate(p.created_at)}</span>
                      {(p.likes > 0 || p.hearts > 0) && (
                        <span className="flex items-center gap-1">
                          {p.likes > 0 && <><ThumbsUp className="w-3 h-3" />{p.likes}</>}
                          {p.hearts > 0 && <><Heart className="w-3 h-3 ml-1" />{p.hearts}</>}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Quick actions */}
                  <div className="flex gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                    {p.status === 'pending' && (
                      <>
                        <Button size="icon" className="h-7 w-7 bg-green-600 hover:bg-green-700" onClick={() => handleAction(p.id, 'approve')} data-testid={`approve-${p.id}`}>
                          <Check className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="icon" variant="destructive" className="h-7 w-7" onClick={() => handleAction(p.id, 'reject')} data-testid={`reject-${p.id}`}>
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    )}
                    {p.status === 'approved' && p.tier === 'basic' && (
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleAction(p.id, 'verify')}>
                        <ShieldCheck className="w-3 h-3 mr-1" /> Verify
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className={`${dialogBg} max-w-lg max-h-[85vh] overflow-y-auto`}>
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Handshake className="w-5 h-5 text-amber-500" />
                  {selected.organization_name}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-3 text-sm">
                <div className="flex flex-wrap gap-2">
                  <Badge className={TYPE_COLORS[selected.partner_type]}>{TYPE_LABELS[selected.partner_type]}</Badge>
                  <Badge className={`${STATUS_COLORS[selected.status]} text-white`}>{selected.status}</Badge>
                  {selected.tier !== 'basic' && <Badge variant="outline">{selected.tier}</Badge>}
                  {selected.featured && <Badge className="bg-amber-100 text-amber-800">Featured</Badge>}
                </div>

                {selected.description && <p className={mutedText}>{selected.description}</p>}

                <div className={`grid grid-cols-2 gap-2 text-xs ${mutedText}`}>
                  {selected.website && <p><strong>Website:</strong> <a href={selected.website} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">{selected.website}</a></p>}
                  {selected.contact_name && <p><strong>Contact:</strong> {selected.contact_name}</p>}
                  {selected.contact_email && <p><strong>Email:</strong> {selected.contact_email}</p>}
                  {selected.contact_phone && <p><strong>Phone:</strong> {selected.contact_phone}</p>}
                  {selected.city && <p><strong>Location:</strong> {selected.city}{selected.state ? `, ${selected.state}` : ''}</p>}
                  {selected.services_offered && <p className="col-span-2"><strong>Services:</strong> {selected.services_offered}</p>}
                  {selected.tax_exempt_id && <p><strong>Tax Exempt:</strong> {selected.tax_exempt_id}</p>}
                  {selected.social_media && <p><strong>Social:</strong> {selected.social_media}</p>}
                </div>

                <div className={`flex gap-4 text-xs ${mutedText}`}>
                  <span>Likes: {selected.likes || 0}</span>
                  <span>Dislikes: {selected.dislikes || 0}</span>
                  <span>Hearts: {selected.hearts || 0}</span>
                  <span>Submitted: {formatDate(selected.created_at)}</span>
                </div>

                {/* Admin Notes */}
                <div>
                  <label className="text-xs font-medium block mb-1">Admin Notes</label>
                  <Textarea
                    defaultValue={selected.admin_notes || ''}
                    onBlur={(e) => handleUpdateNotes(selected.id, e.target.value)}
                    rows={2}
                    className={darkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}
                    placeholder="Internal notes about this partner..."
                  />
                </div>

                {/* Promo Codes (admin) — only render once partner is approved */}
                {selected.status === 'approved' && (
                  <PartnerCodesSection
                    partnerId={selected.id}
                    partnerName={selected.organization_name}
                    darkMode={darkMode}
                  />
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
                  {selected.status === 'pending' && (
                    <>
                      <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleAction(selected.id, 'approve')}>
                        <Check className="w-3.5 h-3.5 mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => handleAction(selected.id, 'reject')}>
                        <X className="w-3.5 h-3.5 mr-1" /> Reject
                      </Button>
                    </>
                  )}
                  {selected.status === 'approved' && selected.tier === 'basic' && (
                    <Button size="sm" variant="outline" onClick={() => handleAction(selected.id, 'verify')}>
                      <ShieldCheck className="w-4 h-4 mr-1" /> Upgrade to Verified
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => handleToggleFeatured(selected.id)}>
                    <Star className="w-4 h-4 mr-1" /> {selected.featured ? 'Unfeature' : 'Feature'}
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(selected.id)} className="ml-auto">
                    <Trash2 className="w-4 h-4 mr-1" /> Delete
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartnerAdmin;
